/**
 * Receptive-directions archetype renderer (fix #252 / #PL-WEB-RUBRICS).
 *
 * 3x3 grid of `display` tiles; the kid taps them in the order the
 * clinician described in `prompt_audio`. The renderer:
 *
 *   - Plays `prompt_audio` once on mount.
 *   - Records the kid's taps as an ordered array of tile ids.
 *   - Watches the self-correction window (`default 4s`); a different
 *     tile tapped within the window flags `self_corrected=true`.
 *   - Calls `onTrialComplete` once the kid has tapped
 *     `expected_sequence.length` tiles, OR they tap a
 *     "Got it!" button (we make it explicit so the kid doesn't
 *     have to fill the sequence exactly).
 *
 * Scoring: exact ordered match against `expected_sequence`
 * (delegated to `scoreReceptiveDirections`).
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  DEFAULT_SELF_CORRECT_WINDOW_MS,
  type ReceptiveDirectionsRubric,
} from '../../../lib/practice/rubrics';
import { scoreReceptiveDirections } from '../../../lib/practice/scoring';
import type { ArchetypePlayerProps, TrialOutcome } from './shared';
import { RefreshIcon } from '../../../components/icons/RefreshIcon';
import { SpeakerIcon } from '../../../components/icons/SpeakerIcon';

interface Props extends ArchetypePlayerProps<ReceptiveDirectionsRubric> {}

export default function ReceptiveDirectionsPlayer({
  rubric,
  trialIndex,
  totalTrials,
  onTrialComplete,
}: Props): ReactElement {
  const windowMs =
    rubric.self_correct_window_ms ?? DEFAULT_SELF_CORRECT_WINDOW_MS;
  const [taps, setTaps] = useState<number[]>([]);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const lastTapAtRef = useRef<number | null>(null);
  const selfCorrectedRef = useRef(false);
  const promptAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setStartedAt(Date.now());
    setTaps([]);
    lastTapAtRef.current = null;
    selfCorrectedRef.current = false;
  }, [trialIndex]);

  function playPrompt(): void {
    if (typeof window === 'undefined') return;
    // Stop any in-flight clip before starting a new one so the
    // kid never hears two prompt audios layered (fix #299).
    if (promptAudioRef.current) {
      promptAudioRef.current.pause();
      promptAudioRef.current.src = '';
    }
    const audio = new Audio(rubric.prompt_audio);
    promptAudioRef.current = audio;
    audio.play().catch(() => {
      // Network or autoplay policy — fail silently; the kid can
      // re-tap the "Play again" button.
    });
  }

  useEffect(() => {
    playPrompt();
    return () => {
      // Pause and release the Audio element on unmount / trial
      // advance so a previous trial's clip cannot leak over the
      // next one (fix #299).
      if (promptAudioRef.current) {
        promptAudioRef.current.pause();
        promptAudioRef.current.src = '';
        promptAudioRef.current = null;
      }
    };
    // playPrompt is stable — only fire on mount/trialIndex change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialIndex]);

  function onTile(idx: number): void {
    const now = Date.now();
    const prev = lastTapAtRef.current;
    setTaps((currentTaps) => {
      // Ignore a duplicate tap of the most recent tile.
      if (
        currentTaps.length > 0 &&
        currentTaps[currentTaps.length - 1] === idx
      ) {
        return currentTaps;
      }
      // While the kid is still entering the sequence, every tap
      // appends. This keeps multi-tile sequences enterable at a
      // normal tap cadence (AC #2).
      if (currentTaps.length < rubric.expected_sequence.length) {
        lastTapAtRef.current = now;
        return [...currentTaps, idx];
      }
      // Sequence is complete: a different tile within the self-
      // correction window replaces the last tap ("re-tap a tile").
      if (
        prev !== null &&
        now - prev < windowMs
      ) {
        selfCorrectedRef.current = true;
        lastTapAtRef.current = now;
        return [...currentTaps.slice(0, -1), idx];
      }
      return currentTaps;
    });
  }

  function onReset(): void {
    setTaps([]);
    selfCorrectedRef.current = false;
    lastTapAtRef.current = null;
  }

  function onSubmit(): void {
    const score = scoreReceptiveDirections(taps, rubric);
    const outcome: TrialOutcome = {
      isCorrect: score.is_correct,
      latencyMs: Date.now() - startedAt,
      selfCorrected: selfCorrectedRef.current,
      usedCue: false,
      response: taps,
      expected: rubric.expected_sequence,
      promptKind: 'picture',
    };
    onTrialComplete(outcome);
  }

  const targetLength = rubric.expected_sequence.length;
  const filled = taps.length;

  // Compute the outcome-as-of-the-current-tap set so every tapped
  // tile in the grid can show the kid a correct/incorrect cue the
  // instant they tap (fix #421 — applying the same immediate-
  // reinforcement pattern as PictureNaming #403 to the sibling
  // archetype renderers). Per-slot scoring: tile at `taps[i]` is
  // correct iff `taps[i] === expected_sequence[i]`. The visual
  // cue persists from the tap until the kid advances; re-tapping
  // within the self-correction window re-evaluates the cue. We
  // deliberately surface right/wrong per tile rather than hiding
  // the outcome — receptive_directions is targeted at kids old
  // enough to process immediate correctness feedback, which is
  // the entire pedagogical point of a sequence-recall trial.
  const tileOutcome = new Map<number, boolean>();
  for (let i = 0; i < taps.length; i += 1) {
    const expectedIdx = rubric.expected_sequence[i];
    if (expectedIdx === undefined) continue;
    tileOutcome.set(taps[i]!, expectedIdx === taps[i]);
  }
  const allTilesCorrect =
    filled === targetLength &&
    tileOutcome.size > 0 &&
    Array.from(tileOutcome.values()).every((v) => v);
  const isOverallCorrect: boolean | null =
    filled === 0
      ? null
      : allTilesCorrect;
  const tappedCorrectCount = Array.from(tileOutcome.values()).filter(
    (v) => v,
  ).length;

  return (
    <main className="screen handoff">
      <div className="auth-hero">
        <header className="screen-header">
          <h1>Trial {trialIndex + 1} / {totalTrials}</h1>
          <p className="tagline">
            Tap the tiles in the order you hear.
          </p>
        </header>
      </div>
      <div className="action-row">
        <button
          type="button"
          className="link-button"
          onClick={playPrompt}
        >
          <SpeakerIcon size={16} aria-hidden="true" />
          <span>Play again</span>
        </button>
        <button
          type="button"
          className="link-button"
          onClick={onReset}
        >
          <RefreshIcon size={16} aria-hidden="true" />
          <span>Start over</span>
        </button>
      </div>
      <div
        className="grid-3x3"
        role="group"
        aria-label="Tile grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '8px',
          maxWidth: '320px',
          margin: '0 auto',
        }}
      >
        {rubric.grid.map((tile, idx) => {
          const tappedOrder = taps.indexOf(idx);
          const tapped = tappedOrder >= 0;
          const thisTileCorrect = tileOutcome.get(idx);
          const outcomeClass = tapped && thisTileCorrect === true
            ? ' selected correct'
            : tapped && thisTileCorrect === false
              ? ' selected incorrect'
              : filled > 0 && !tapped
                ? ' muted'
                : '';
          return (
            <button
              type="button"
              key={`tile-${idx}`}
              className={`snap-button${outcomeClass}`}
              onClick={(): void => onTile(idx)}
              aria-pressed={tapped}
              aria-label={
                tapped && thisTileCorrect === true
                  ? `Tile ${tile}, tap ${tappedOrder + 1}, correct`
                  : tapped && thisTileCorrect === false
                    ? `Tile ${tile}, tap ${tappedOrder + 1}, wrong; expected ${rubric.grid[rubric.expected_sequence[tappedOrder] ?? -1] ?? 'unknown'}`
                    : tapped
                      ? `Tile ${tile}, tap ${tappedOrder + 1}`
                      : filled > 0
                        ? `Tile ${tile}`
                        : tile
              }
              style={{ minHeight: '64px', fontSize: '1.6rem' }}
            >
              {tile}
            </button>
          );
        })}
      </div>
      <p className="tagline">
        Tapped {filled} of {targetLength}
        {filled > 0
          ? ` (${tappedCorrectCount} correct)`
          : ''}
      </p>
      {filled > 0 ? (
        <button
          type="button"
          className="snap-button"
          onClick={onSubmit}
          aria-label={
            isOverallCorrect === true
              ? `Got it! — all ${tappedCorrectCount} tiles correct`
              : isOverallCorrect === false
                ? `Got it! — ${tappedCorrectCount} of ${filled} tiles correct`
                : filled === targetLength
                  ? 'Got it!'
                  : 'Got it (early)'
          }
        >
          {filled === targetLength ? 'Got it!' : 'Got it (early)'}
        </button>
      ) : null}
    </main>
  );
}
